// Approximate market rates (INR per 1 unit of foreign currency). These are
// intentionally NOT live/exact rates - see MARKUP_MULTIPLIER below.
export const CURRENCIES = [
  { code: "INR", symbol: "₹", name: "Indian Rupee", rateInrPerUnit: 1 },
  { code: "USD", symbol: "$", name: "US Dollar", rateInrPerUnit: 83 },
  { code: "AUD", symbol: "A$", name: "Australian Dollar", rateInrPerUnit: 55 },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar", rateInrPerUnit: 62 },
  { code: "SAR", symbol: "﷼", name: "Saudi Riyal", rateInrPerUnit: 22 },
  { code: "JPY", symbol: "¥", name: "Japanese Yen", rateInrPerUnit: 0.56 },
  { code: "GBP", symbol: "£", name: "British Pound", rateInrPerUnit: 105 },
  { code: "EUR", symbol: "€", name: "Euro", rateInrPerUnit: 90 },
  { code: "AED", symbol: "د.إ", name: "UAE Dirham", rateInrPerUnit: 22.6 },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar", rateInrPerUnit: 61 },
];

// Maps ISO 3166-1 alpha-2 country codes to a supported currency. Countries
// not listed here fall back to USD (see currencyForCountry below).
const COUNTRY_TO_CURRENCY = {
  IN: "INR",
  US: "USD",
  AU: "AUD",
  SG: "SGD",
  SA: "SAR",
  JP: "JPY",
  GB: "GBP",
  AE: "AED",
  CA: "CAD",
  // Eurozone
  DE: "EUR", FR: "EUR", IT: "EUR", ES: "EUR", NL: "EUR", BE: "EUR",
  AT: "EUR", IE: "EUR", PT: "EUR", FI: "EUR", GR: "EUR", LU: "EUR",
  SK: "EUR", SI: "EUR", EE: "EUR", LV: "EUR", LT: "EUR", CY: "EUR",
  MT: "EUR", HR: "EUR",
};

export function currencyForCountry(countryCode) {
  const code = COUNTRY_TO_CURRENCY[(countryCode || "").toUpperCase()] || "USD";
  return CURRENCIES.find((c) => c.code === code) || CURRENCIES.find((c) => c.code === "USD");
}

// Deliberate pricing markup for non-INR currencies - display price is 1.5x
// the fair-market conversion, per product decision (not a live FX feed).
const MARKUP_MULTIPLIER = 1.5;

function roundToTen(n) {
  if (n <= 0) return 0;
  const rounded = Math.round(n / 10) * 10;
  return rounded === 0 ? 10 : rounded;
}

// Charm pricing: snaps to the nearest number ending in 5 or 9 (e.g. 15, 19,
// 25, 29...) instead of a plain multiple of 10. Used for add-on/per-unit
// prices shown in non-INR currencies, where a round-number conversion looks
// unintentional (e.g. "$20") rather than deliberately priced (e.g. "$19").
function roundToNiceEnding(n) {
  if (n <= 0) return 5;
  const base = Math.floor(n / 10) * 10;
  const candidates = [base - 1, base + 5, base + 9, base + 15].filter((c) => c > 0);
  return candidates.reduce((best, c) => (Math.abs(n - c) < Math.abs(n - best) ? c : best), candidates[0]);
}

export function convertFromINR(inrAmount, currency) {
  if (currency.code === "INR") return Math.round(inrAmount);
  const fairAmount = inrAmount / currency.rateInrPerUnit;
  return roundToTen(fairAmount * MARKUP_MULTIPLIER);
}

// Same 1.5x markup as convertFromINR, but rounded to a charm-priced ending
// (5/9) instead of a flat multiple of 10 - for add-on per-unit pricing.
export function convertFromINRAddon(inrAmount, currency) {
  if (currency.code === "INR") return Math.round(inrAmount);
  const fairAmount = inrAmount / currency.rateInrPerUnit;
  return roundToNiceEnding(fairAmount * MARKUP_MULTIPLIER);
}

// Configurable variant used by the lifetime-deal page: fair conversion plus
// `markup` (1.2 = +20%), snapped to the nearest multiple of `step` (e.g. 50).
// INR is returned unchanged.
export function convertFromINRStepped(inrAmount, currency, { markup, step }) {
  if (currency.code === "INR") return Math.round(inrAmount);
  const marked = (inrAmount / currency.rateInrPerUnit) * markup;
  return Math.max(step, Math.round(marked / step) * step);
}

export function formatCurrency(inrAmount, currency) {
  const amount = convertFromINR(inrAmount, currency);
  return `${currency.symbol}${amount.toLocaleString("en-US")}`;
}

export function formatCurrencyCompact(inrAmount, currency) {
  const amount = convertFromINR(inrAmount, currency);
  if (currency.code === "INR") {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount}`;
  }
  if (amount >= 1000000) return `${currency.symbol}${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `${currency.symbol}${(amount / 1000).toFixed(1)}K`;
  return `${currency.symbol}${amount}`;
}
